import React, { useState } from 'react';
import { addTenant, removeTenant } from '../services/featureFlagsApi';

const TenantManager = ({ flagId, allowedTenants, onUpdate }) => {
  const [newTenant, setNewTenant] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!flagId) {
    return (
      <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
        <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Tenants</h3>
        <div className="p-3 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 rounded-lg text-sm">
          Error: Feature flag ID is missing. Please refresh the page.
        </div>
      </div>
    );
  }

  const handleAddTenant = async (e) => {
    e.preventDefault();
    if (!newTenant.trim()) {
      setError('Tenant name is required');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await addTenant(flagId, newTenant.trim());
      setNewTenant('');
      onUpdate();
    } catch (err) {
      setError(err.details?.error || err.message || 'Failed to add tenant');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveTenant = async (tenant) => {
    if (!window.confirm(`Are you sure you want to remove tenant "${tenant}"?`)) return;
    try {
      setSaving(true);
      setError(null);
      await removeTenant(flagId, tenant);
      onUpdate();
    } catch (err) {
      setError(err.details?.error || err.message || 'Failed to remove tenant');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
      <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b border-gray-200 dark:border-slate-700">Tenants ({allowedTenants.length})</h3>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 rounded-lg text-sm">{error}</div>
      )}

      <form onSubmit={handleAddTenant} className="flex flex-col sm:flex-row gap-2 mb-4">
        <input
          type="text"
          value={newTenant}
          onChange={(e) => setNewTenant(e.target.value)}
          placeholder="Enter tenant name"
          disabled={saving}
          className="flex-1 px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
        />
        <button
          type="submit"
          disabled={saving || !newTenant.trim()}
          className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-60 shrink-0"
        >
          Add Tenant
        </button>
      </form>

      {allowedTenants.length === 0 ? (
        <p className="text-gray-500 dark:text-slate-400 italic text-center py-4">No tenants configured</p>
      ) : (
        <ul className="space-y-2">
          {allowedTenants.map((tenant) => (
            <li
              key={tenant}
              className="flex justify-between items-center p-3 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg"
            >
              <span className="font-medium text-gray-800 dark:text-gray-200">{tenant}</span>
              <button
                onClick={() => handleRemoveTenant(tenant)}
                disabled={saving}
                className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-60"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TenantManager;
